/**
 * UserActionsMenu — Tier-0 disclosure menu of admin actions on
 * `/users/:userId`. Phase 5 of SCRUM-108.
 *
 * Actions (each behind a `<RequirePermission>` gate):
 *   - "Send password reset" → `useTriggerPasswordReset`. Gate:
 *     `identity.users.reset_password`.
 *   - "Resend verification email" → `useResendVerification`. Gate:
 *     `identity.users.verification.resend`. Additionally hidden when
 *     the target's `emailVerifiedAt` is non-null (resend on an already-
 *     verified user is a 422 from the BE; hide the affordance upfront).
 *
 * Both actions confirm via an inline `role="dialog"` panel before
 * firing. Success → `role="status"` banner with the redacted recipient
 * (e.g. `m***@harborkey.co`). Specific failures (`rateLimited`,
 * `alreadyVerified`, `forbidden`) surface as `role="alert"` banners.
 *
 * Per Hybrid Z, this Tier-0 component lives in the identity slice's
 * `_components/`. Its inputs (`useTriggerPasswordReset`,
 * `useResendVerification`) live alongside in `api/`.
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';
import { useTriggerPasswordReset } from '../api/use-trigger-password-reset.js';
import { useResendVerification } from '../api/use-resend-verification.js';
import type { OrgMemberDetailView } from '../api/types.js';

interface UserActionsMenuProps {
  readonly target: OrgMemberDetailView;
}

type ConfirmState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reset' }
  | { readonly kind: 'resend' };

export function UserActionsMenu({
  target,
}: UserActionsMenuProps): React.ReactElement {
  const reset = useTriggerPasswordReset(target.userId);
  const resend = useResendVerification(target.userId);

  const [isOpen, setIsOpen] = React.useState(false);
  const [confirm, setConfirm] = React.useState<ConfirmState>({ kind: 'idle' });
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

  const isVerified = target.emailVerifiedAt !== null;

  function closeConfirm(): void {
    setConfirm({ kind: 'idle' });
  }

  async function performReset(): Promise<void> {
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      const { sentTo } = await reset.mutateAsync();
      setStatusMessage(
        sentTo.length > 0
          ? `Password-reset email sent to ${sentTo}.`
          : 'Password-reset email sent.',
      );
    } catch (e: unknown) {
      setErrorMessage(passwordResetErrorMessage(e));
    } finally {
      closeConfirm();
      setIsOpen(false);
    }
  }

  async function performResend(): Promise<void> {
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      const { sentTo } = await resend.mutateAsync();
      setStatusMessage(
        sentTo.length > 0
          ? `Verification email re-sent to ${sentTo}.`
          : 'Verification email re-sent.',
      );
    } catch (e: unknown) {
      setErrorMessage(resendErrorMessage(e));
    } finally {
      closeConfirm();
      setIsOpen(false);
    }
  }

  // Compose both permission gates around a synthetic visibility
  // probe so the trigger button disappears entirely when neither
  // permission is granted (rather than rendering a useless empty menu).
  // The probe uses `<RequirePermission>` as-is, falling back to null.
  // The menu items themselves are also gated.
  return (
    <RequirePermission slug="identity.users.reset_password" fallback={
      <RequirePermission slug="identity.users.verification.resend" fallback={null}>
        <MenuChrome
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          confirm={confirm}
          setConfirm={setConfirm}
          isVerified={isVerified}
          errorMessage={errorMessage}
          statusMessage={statusMessage}
          performReset={performReset}
          performResend={performResend}
          resetPending={reset.isPending}
          resendPending={resend.isPending}
          dismissError={() => setErrorMessage(null)}
          dismissStatus={() => setStatusMessage(null)}
        />
      </RequirePermission>
    }>
      <MenuChrome
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        confirm={confirm}
        setConfirm={setConfirm}
        isVerified={isVerified}
        errorMessage={errorMessage}
        statusMessage={statusMessage}
        performReset={performReset}
        performResend={performResend}
        resetPending={reset.isPending}
        resendPending={resend.isPending}
        dismissError={() => setErrorMessage(null)}
        dismissStatus={() => setStatusMessage(null)}
      />
    </RequirePermission>
  );
}

interface MenuChromeProps {
  readonly isOpen: boolean;
  readonly setIsOpen: (v: boolean) => void;
  readonly confirm: ConfirmState;
  readonly setConfirm: (v: ConfirmState) => void;
  readonly isVerified: boolean;
  readonly errorMessage: string | null;
  readonly statusMessage: string | null;
  readonly performReset: () => void | Promise<void>;
  readonly performResend: () => void | Promise<void>;
  readonly resetPending: boolean;
  readonly resendPending: boolean;
  readonly dismissError: () => void;
  readonly dismissStatus: () => void;
}

function MenuChrome({
  isOpen,
  setIsOpen,
  confirm,
  setConfirm,
  isVerified,
  errorMessage,
  statusMessage,
  performReset,
  performResend,
  resetPending,
  resendPending,
  dismissError,
  dismissStatus,
}: MenuChromeProps): React.ReactElement {
  return (
    <div className="relative flex flex-col items-end gap-2">
      <Button
        variant="secondary"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        More actions
      </Button>

      {isOpen ? (
        <div
          role="menu"
          aria-label="More actions"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-2 text-sm shadow-md"
        >
          <RequirePermission slug="identity.users.reset_password">
            {/*
             * ADR frontend-0004 exemption: menuitem inside `role="menu"`
             * has the WAI-ARIA disclosure-pattern semantics that kit
             * <Button> (which renders a styled pill) does not match.
             * A future kit <MenuItem> primitive should replace this on
             * the second consumer (e.g. row-action menus in tables).
             */}
            {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
            <button
              type="button"
              role="menuitem"
              disabled={resetPending}
              onClick={() => setConfirm({ kind: 'reset' })}
              className="block w-full text-left rounded-md px-3 py-2 text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--brand-500)] focus-visible:outline-offset-2"
            >
              Send password reset
            </button>
          </RequirePermission>
          {!isVerified ? (
            <RequirePermission slug="identity.users.verification.resend">
              {/* ADR frontend-0004 exemption: same menuitem rationale. */}
              {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
              <button
                type="button"
                role="menuitem"
                disabled={resendPending}
                onClick={() => setConfirm({ kind: 'resend' })}
                className="block w-full text-left rounded-md px-3 py-2 text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--brand-500)] focus-visible:outline-offset-2"
              >
                Resend verification email
              </button>
            </RequirePermission>
          ) : null}
        </div>
      ) : null}

      {errorMessage !== null ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-1 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm text-[var(--fg-secondary)]"
        >
          <p>{errorMessage}</p>
          <Button variant="ghost" size="sm" onClick={dismissError}>
            Dismiss
          </Button>
        </div>
      ) : null}
      {statusMessage !== null ? (
        <div
          role="status"
          className="flex flex-col items-start gap-1 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm text-[var(--fg-secondary)]"
        >
          <p>{statusMessage}</p>
          <Button variant="ghost" size="sm" onClick={dismissStatus}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {confirm.kind !== 'idle' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-actions-confirm-title"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          <h3
            id="user-actions-confirm-title"
            className="text-base font-semibold text-[var(--fg-primary)]"
          >
            {confirm.kind === 'reset'
              ? 'Send password reset?'
              : 'Resend verification email?'}
          </h3>
          <p className="mt-2">
            {confirm.kind === 'reset'
              ? 'We’ll email a one-time password-reset link to this user.'
              : 'We’ll re-send the email-verification link to this user.'}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirm({ kind: 'idle' })}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={confirm.kind === 'reset' ? resetPending : resendPending}
              onClick={() => {
                if (confirm.kind === 'reset') {
                  void performReset();
                } else {
                  void performResend();
                }
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function passwordResetErrorMessage(e: unknown): string {
  const err = e as { kind?: string };
  if (err.kind === 'rateLimited') {
    return 'Too many password-reset requests for this admin. Try again in a few minutes (rate limit).';
  }
  if (err.kind === 'forbidden') {
    return 'You don’t have permission to trigger password resets.';
  }
  if (err.kind === 'notFound') {
    return 'This user could not be found.';
  }
  return 'We couldn’t send the password-reset email. Try again in a moment.';
}

function resendErrorMessage(e: unknown): string {
  const err = e as { kind?: string };
  if (err.kind === 'alreadyVerified') {
    return 'This email is already verified — no need to resend.';
  }
  if (err.kind === 'rateLimited') {
    return 'Too many verification re-sends for this admin. Try again in a few minutes.';
  }
  if (err.kind === 'forbidden') {
    return 'You don’t have permission to resend verification emails.';
  }
  if (err.kind === 'notFound') {
    return 'This user could not be found.';
  }
  return 'We couldn’t resend the verification email. Try again in a moment.';
}
